/**
 * Generic async data-fetching hook.
 *
 * Provides `data`, `error`, `loading`, and `refetch` – the building block
 * for all domain-specific hooks. Replaces the per-request `isRequesting`,
 * `ok`, `data`, and `hasRequested` booleans that Wized tracked internally.
 */

import { useCallback, useEffect, useRef, useState } from "react";

interface AsyncState<T> {
  data: T | null;
  error: string | null;
  loading: boolean;
}

interface UseAsyncReturn<T> extends AsyncState<T> {
  /** Re-run the fetcher and show loading state. */
  refetch: () => Promise<void>;
  /** Re-run the fetcher without forcing loading state when data already exists. */
  revalidate: () => Promise<void>;
  /** Imperatively set the data (e.g. after a mutation). */
  setData: (data: T | null | ((prev: T | null) => T | null)) => void;
}

/**
 * @param fetcher – Async function that returns `{ data, error }` (Supabase convention).
 * @param deps – Dependency array. Fetcher re-runs when these change. Pass `null` to skip.
 * @param options.initialData – Pre-populated data for instant render (stale-while-revalidate).
 */
export function useAsync<T>(
  fetcher: () => Promise<{ data: T | null; error: { message: string } | null }>,
  deps: unknown[] | null = [],
  options?: { initialData?: T },
): UseAsyncReturn<T> {
  const initial = options?.initialData ?? null;

  const [state, setState] = useState<AsyncState<T>>({
    data: initial,
    error: null,
    // If we have initial data, don't show loading (data is already renderable)
    loading: initial ? false : deps !== null,
  });

  const fetcherRef = useRef(fetcher);
  fetcherRef.current = fetcher;

  const run = useCallback(async (forceLoading = false) => {
    // If we already have data (initial), don't show loading — just revalidate silently
    // Unless forceLoading is true (explicit refetch), in which case always show loading
    setState((prev) => prev.data && !forceLoading
      ? { ...prev, error: null }
      : { ...prev, loading: true, error: null },
    );
    const { data, error } = await fetcherRef.current();
    setState({ data, error: error?.message ?? null, loading: false });
  }, []);

  const depsKey =
    deps === null
      ? null
      : deps
          .map((value) => {
            if (typeof value === "string") return value;
            if (
              typeof value === "number" ||
              typeof value === "boolean" ||
              value === null ||
              value === undefined
            ) {
              return String(value);
            }
            try {
              return JSON.stringify(value);
            } catch {
              return String(value);
            }
          })
          .join("\u001f");
  const isEnabled = deps !== null;

  // Auto-fetch on mount / dep change
  useEffect(() => {
    if (!isEnabled) return;
    run();
  }, [depsKey, isEnabled, run]);

  const setData = useCallback((data: T | null | ((prev: T | null) => T | null)) => {
    setState((prev) => ({
      ...prev,
      data: typeof data === "function"
        ? (data as (prev: T | null) => T | null)(prev.data)
        : data,
    }));
  }, []);

  const refetch = useCallback(() => run(true), [run]);
  const revalidate = useCallback(() => run(false), [run]);

  return { ...state, refetch, revalidate, setData };
}

/**
 * Hook for mutations (actions triggered by user interaction).
 * Does not auto-fetch. Returns `execute` instead of `refetch`.
 */
export function useMutation<TArgs extends unknown[], TResult>(
  mutationFn: (
    ...args: TArgs
  ) => Promise<{
    data: TResult | null;
    error: { message: string } | null;
  }>,
) {
  const [state, setState] = useState<AsyncState<TResult>>({
    data: null,
    error: null,
    loading: false,
  });

  const mutationRef = useRef(mutationFn);
  mutationRef.current = mutationFn;

  const execute = useCallback(async (...args: TArgs) => {
    setState({ data: null, error: null, loading: true });
    const { data, error } = await mutationRef.current(...args);
    setState({ data, error: error?.message ?? null, loading: false });
    return { data, error: error?.message ?? null };
  }, []);

  const reset = useCallback(() => {
    setState({ data: null, error: null, loading: false });
  }, []);

  return { ...state, execute, reset };
}
